import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck } from 'lucide-react';
import { WS_EVENT, type NotificationPayload } from '@kick/types';
import { cn } from '@/lib/cn';
import { useAuth } from '@/lib/auth-store';
import { sonarAviso } from '@/lib/sonido';
import { useEventoSocket } from '@/lib/socket';
import {
  useLeerTodas,
  useMarcarLeida,
  useNotificaciones,
  useSinLeer,
  type Notificacion,
} from '@/lib/notificaciones';

/**
 * La campana.
 *
 * Guarda lo que el canal en vivo enseña de paso: un aviso que solo aparece
 * como mensaje flotante se pierde para quien estaba en otra pestaña. Aquí
 * queda hasta que se lee.
 */
export function Campana() {
  const accessToken = useAuth((e) => e.accessToken);
  const activo = !!accessToken;
  const [abierta, setAbierta] = useState(false);
  const contenedor = useRef<HTMLDivElement>(null);
  const cliente = useQueryClient();
  const navegar = useNavigate();

  const sinLeer = useSinLeer(activo);
  const lista = useNotificaciones(activo && abierta);
  const marcarLeida = useMarcarLeida();
  const leerTodas = useLeerTodas();

  /**
   * Lo que llega en vivo entra sin recargar.
   *
   * Se invalida en vez de insertar a mano: el aviso ya viene con su
   * identificador, pero mantener dos copias de la lista —la del servidor y la
   * que se va parcheando aquí— es como aparecen los duplicados al reconectar.
   */
  useEventoSocket<NotificationPayload>(WS_EVENT.NOTIFICATION, () => {
    void cliente.invalidateQueries({ queryKey: ['notificaciones'] });
    // El aviso también suena: la campana sola no se ve con la pantalla apagada.
    sonarAviso();
  });

  // Cerrar al tocar fuera: un panel que se queda abierto tapa la navegación.
  useEffect(() => {
    if (!abierta) return;

    const fuera = (e: MouseEvent): void => {
      if (!contenedor.current?.contains(e.target as Node)) setAbierta(false);
    };
    const escape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setAbierta(false);
    };

    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('keydown', escape);
    };
  }, [abierta]);

  if (!activo) return null;

  const total = sinLeer.data ?? 0;

  const abrir = (n: Notificacion): void => {
    if (!n.readAt) marcarLeida.mutate(n.id);
    setAbierta(false);

    const orderId = n.data?.orderId;
    if (typeof orderId === 'string') navegar(`/pedidos/${orderId}`);
  };

  return (
    <div ref={contenedor} className="relative">
      <button
        onClick={() => setAbierta((v) => !v)}
        aria-label={total > 0 ? `Notificaciones, ${total} sin leer` : 'Notificaciones'}
        aria-expanded={abierta}
        className="relative rounded-kick p-2 text-white/80 transition hover:bg-white/10"
      >
        <Bell className="h-5 w-5" />
        {total > 0 && (
          <span className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-kick-red px-1 text-[11px] font-bold text-white">
            {total > 9 ? '9+' : total}
          </span>
        )}
      </button>

      {abierta && (
        <div className="absolute right-0 top-full z-40 mt-2 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-kick bg-white shadow-xl">
          <div className="flex items-center justify-between border-b border-kick-gray/15 px-4 py-3">
            <p className="font-bold text-kick-dark">Notificaciones</p>
            {total > 0 && (
              <button
                onClick={() => leerTodas.mutate()}
                className="flex items-center gap-1 text-xs font-semibold text-kick-red hover:underline"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Marcar todas
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {lista.isLoading && (
              <p className="px-4 py-8 text-center text-sm text-kick-gray">Cargando…</p>
            )}

            {lista.data?.items.length === 0 && (
              <p className="px-4 py-8 text-center text-sm text-kick-gray">
                Nada por aquí todavía.
              </p>
            )}

            {lista.data?.items.map((n) => (
              <button
                key={n.id}
                onClick={() => abrir(n)}
                className={cn(
                  'block w-full border-b border-kick-gray/10 px-4 py-3 text-left transition last:border-0 hover:bg-kick-light',
                  !n.readAt && 'bg-red-50/60',
                )}
              >
                <div className="flex items-start gap-2">
                  {!n.readAt && (
                    <span
                      aria-hidden
                      className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-kick-red"
                    />
                  )}
                  <div className={cn('min-w-0 flex-1', n.readAt && 'pl-4')}>
                    <p className="truncate text-sm font-semibold text-kick-ink">{n.title}</p>
                    <p className="text-sm text-kick-gray">{n.body}</p>
                    <p className="mt-0.5 text-xs text-kick-gray">{haceCuanto(n.createdAt)}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * «Hace 5 min» en vez de una fecha.
 *
 * Para un aviso lo que importa es si es de ahora o de ayer, no el minuto
 * exacto. La fecha completa obliga a hacer la resta mentalmente.
 */
function haceCuanto(iso: string): string {
  const minutos = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);

  if (minutos < 1) return 'Ahora mismo';
  if (minutos < 60) return `Hace ${minutos} min`;
  if (minutos < 60 * 24) return `Hace ${Math.floor(minutos / 60)} h`;

  return new Date(iso).toLocaleDateString('es-CO', {
    day: 'numeric',
    month: 'short',
    timeZone: 'America/Bogota',
  });
}
